/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_USER_LIST } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

interface userListProp {
  criteria?: any;
  limit?: any;
  skip?: any;
}

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let whereCls: any = [];
    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        whereCls.push(...this.getMultiFilter(args?.criteria));
      } else {
        whereCls.push(...this.getNormalFilter(args?.criteria));
      }
    }
    if (this.getFilter(args?.criteria) !== null) {
      whereCls.push(this.getFilter(args?.criteria));
    }
    // This is required
    whereCls.push({
      column: "email",
      value: [null],
      op: "NotEqual",
    });
    console.log("wherecls==>", whereCls);
    try {
      if (client) {
        const res = await client.query({
          query: GET_USER_LIST,
          variables: {
            input: {
              where: whereCls,
              order: [
                {
                  direction: "DESC",
                  column: "createdAt",
                },
              ],
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              invitedByMe: false,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _totalPages = res?.data?.getUserList?.totalCount
            ? parseInt(res?.data?.getUserList?.totalCount) / 10
            : 1;

          let _currentPage =
            res?.data?.getUserList?.currentPage /
            res?.data?.getUserList?.pageSize +
            1;
          let _rowData = this.formatUserTable(
            res?.data?.getUserList?.data,
            res?.data?.getUserList?.data
          );
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getUserList?.data?.length <= 0,
            // studentGroup: holderGroups,
            activePage: _currentPage,
            pageSize: res?.data?.getUserList?.pageSize,
            totalCount: res?.data?.getUserList?.totalCount,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      return {
        isEmpty: true,
      };
    }
  }

  private _getRoles = (role: any, roles: any) => {
    let _res: any[] = [];
    roles &&
      Array.isArray(roles) &&
      roles.length > 0 &&
      roles.map((item: any, index: number) => {
        role &&
          Array.isArray(role) &&
          role.length > 0 &&
          role.map((ele: any, eleindex: number) => {
            if (ele === item?.uuid) {
              _res.push(item?.name);
            }
          });
      });

    return _res;
  };

  private formatUserTable(data: any, roles: any[]) {
    let _rowData: any =
      Array.isArray(data) &&
      data.map((item: any) => {
        return {
          id: item?.id,
          vuid: item?.vuid,
          // status: item?.status,
          status: this.getFormattedStatus(item?.accountStatus, item?.suspended),
          name: `${item?.firstName ?? "-"} ${item?.lastName ?? ""}`,
          email: item?.email,
          group: item?.group,
          image: item?.img,
          gender: item?.gender ? item?.gender : "-",
          invitedBy: item?.creatorUser?.fullName
            ? item?.creatorUser?.fullName
            : "-",
          verified: item?.emailVerified,
          role: this._getRole(item?.role),
        };
      });
    return _rowData;
  }

  private _getRole(roles: any[]) {
    let _res: any[] = [];
    roles &&
      Array.isArray(roles) &&
      roles.length > 0 &&
      roles.map((item: any, index: number) => {
        _res.push(item?.name);
      });
    return _res;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }

  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "blacklisted";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "created";
        case "USER_INVITED":
          return "invited";
        case "USER_VERIFIED":
          return "active";
        case "USER_ACC_CREATED":
          return "active";
        case "SUSPENDED":
          return "suspended";
        case "BLACKLISTED":
          return "blacklisted";
        default:
          return "-";
      }
    }
  }

  private getFilter(criteria: any) {
    if (criteria?.fullName) {
      return {
        column: "fullName",
        op: "Contains",
        value: criteria?.fullName.toLowerCase(),
      };
    }
    if (criteria?.email) {
      return {
        column: "email",
        op: "Contains",
        value: criteria?.email.toLowerCase(),
      };
    }
    return null;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private getNormalFilter(criteria: any) {
    const resultArray = [];
    if (
      criteria?.accountStatus__in &&
      criteria?.accountStatus__in !== null &&
      criteria?.accountStatus__in !== "undefined"
    ) {
      if (criteria?.accountStatus__in === "SUSPENDED") {
        resultArray.push({
          column: "suspended",
          op: "Equal",
          value: true,
        });
      } else {
        if (criteria?.accountStatus__in === "USER_ACC_CREATED") {
          resultArray.push({
            column: "accountStatus",
            op: "Equal",
            value: "USER_ACC_CREATED",
          });
        } else {
          resultArray.push({
            column: "accountStatus",
            op: "Equal",
            value: criteria?.accountStatus__in,
          });
        }
      }
    }
    return resultArray;
  }
  private getMultiFilter(criteria: any) {
    const options = [
      {
        label: "Masquerader",
        value: "fe93dab0-424b-4aa3-9efa-c001b4c04444",
      },
      {
        label: "Admin",
        value: "fe93dab0-424b-4aa3-9efa-c001b4c03013",
      },
      {
        label: "committeeMember",
        value: "99403c8b-abc1-4796-b61f-78e0c4022792",
      },
      {
        label: "cashier",
        value: "f5bebfee-dc0a-460a-9a3f-c6cde8fed944",
      },
      {
        label: "supervisor",
        value: "ca9521c7-d584-43b8-8c90-79aa3e11bddc",
      },
      {
        label: "cashSupervisor",
        value: "5dbff659-66e4-4a66-b03c-f9b2d3dd6de6",
      },
    ];

    const transformedCriteria: any = [];
    Object.values(criteria).forEach((item: any) => {
      const column = item.filters.inputData.id;
      const op = item.filters.inputData.label;

      // Determine the value based on the available filter keys
      let value = "";
      if (column === "role" && "Equal" in item.filters) {
        const label = item.filters.Equal;
        const option = options.find((opt) => opt.label === label);
        if (option) {
          value = option.value.toLowerCase();
        }
      } else if ("Contains" in item.filters) {
        value = item.filters.Contains.toLowerCase();
      } else if ("Equal" in item.filters) {
        value = item.filters.Equal.toLowerCase();
      } // Add more conditions for other possible filter keys

      transformedCriteria.push({
        column,
        value,
        op,
      });
    });

    return transformedCriteria;
  }
}
